// In src/components/QuestDetailsModal.js

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import TimerSessionModal from './TimerSessionModal'; // NEW
import BicepIcon from './icons/BicepIcon';
import CutleryIcon from './icons/CutleryIcon';
import MeditateIcon from './icons/MeditateIcon';
import FootstepsIcon from './icons/FootstepsIcon';
import MoonIcon from './icons/MoonIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { Brain, Flag, Maximize2, X } from 'lucide-react';
import { getImagesForQuest, reportQuestImage } from '../api/dataService'; // NEW
import { ImageKitProvider, Image } from '@imagekit/react'; // NEW
import config from '../config/config'; // NEW
import { getMediaUrl, resolveImageKitUrl } from '../utils/mediaUtils';
import { getMediaUnavailableText } from '../utils/mediaAvailability';
import { buildQuestImageReportKey, getAiImageNotice } from '../utils/questImageReportUtils';
import { useToast } from '../context/ToastContext';
import { useLocale } from '../context/LocaleContext';

const ImagePlaceholder = () => (
  <div className="w-full h-64 bg-slate-800 rounded-xl animate-pulse"></div>
);

const WorkoutImagePlaceholder = () => (
  <div className="w-full h-48 bg-slate-800 rounded-xl animate-pulse"></div>
);

const MediaUnavailablePlaceholder = ({ className = 'h-64' }) => {
  const { t } = useLocale();
  return (
    <div className={`w-full ${className} rounded-xl border border-dashed border-white/10 bg-slate-950/60 flex items-center justify-center px-4 text-center text-sm font-semibold text-gray-300`}>
      {getMediaUnavailableText(t)}
    </div>
  );
};

const QuestImageFrame = ({
  mediaUrl,
  mediaType = 'image',
  alt,
  children,
  onExpand,
  onReport,
  isReporting,
  isReported,
  t,
}) => (
  <div className="space-y-2">
    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-slate-950">
      {children}
      <div className="absolute left-2 top-2 max-w-[calc(100%-3.25rem)] rounded-md bg-slate-950/85 px-2 py-1 text-[11px] font-semibold leading-4 text-slate-100 shadow-lg backdrop-blur">
        {getAiImageNotice(t)}
      </div>
      <button
        type="button"
        onClick={() => onExpand({ mediaUrl, mediaType, alt })}
        className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-slate-950/85 text-white shadow-lg transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
        aria-label={t('questDetails.expandMedia', 'Expand {label}', { label: alt })}
      >
        <Maximize2 className="h-4 w-4" />
      </button>
    </div>
    <button
      type="button"
      onClick={onReport}
      disabled={isReporting || isReported}
      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300/25 bg-amber-400/10 px-3 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-400/20 disabled:cursor-not-allowed disabled:opacity-55"
    >
      <Flag className="h-4 w-4" />
      {isReported
        ? t('questDetails.mediaReported', 'Media reported')
        : isReporting
          ? t('questDetails.reportingMedia', 'Reporting media...')
          : t('questDetails.reportInaccurateMedia', 'Report inaccurate media')}
    </button>
  </div>
);

const formatSleepMinutes = (minutes) => {
  const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;
  return `${hours}h ${String(remainingMinutes).padStart(2, '0')}m`;
};

// Helper component to render Workout/Mindfulness details
const WorkoutDetails = ({ details, isLoading, onExpandImage, onReportImage, isImageReporting, isImageReported, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4 text-center">
      <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
        <p className="text-sm text-gray-400">{t('questDetails.time', 'Time')}</p>
        <p className="font-bold text-white">{details.estimatedTime || t('questDetails.na', 'N/A')}</p>
      </div>
      <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
        <p className="text-sm text-gray-400">{t('questDetails.calories', 'Calories')}</p>
        <p className="font-bold text-white">{details.estimatedCalories ? `${details.estimatedCalories} kcal` : t('questDetails.na', 'N/A')}</p>
      </div>
    </div>
    <div className="space-y-3">
      {details.exercises?.map((exercise, index) => (
        <div key={index} className="bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl">
          {/* NEW: Image rendering logic */}
          <div className="mb-3">
            {isLoading ? (
              <WorkoutImagePlaceholder />
            ) : exercise.videoUrl ? (
              <QuestImageFrame
                mediaUrl={exercise.videoUrl}
                mediaType="video"
                alt={exercise.name || t('questDetails.exerciseVideoAlt', 'Exercise video')}
                onExpand={onExpandImage}
                onReport={() => onReportImage({ scope: 'exercise', exerciseIndex: index, mediaType: 'video', mediaUrl: exercise.videoUrl })}
                isReporting={isImageReporting({ scope: 'exercise', exerciseIndex: index, mediaType: 'video', imageUrl: exercise.videoUrl })}
                isReported={isImageReported({ scope: 'exercise', exerciseIndex: index, mediaType: 'video', imageUrl: exercise.videoUrl })}
                t={t}
              >
                <video
                  data-testid="exercise-video"
                  src={getMediaUrl(exercise.videoUrl, config.media.workerBase)}
                  poster={exercise.posterUrl ? getMediaUrl(exercise.posterUrl, config.media.workerBase) : undefined}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-64 object-cover"
                />
              </QuestImageFrame>
            ) : exercise.imageUrl ? (
              <QuestImageFrame
                mediaUrl={exercise.imageUrl}
                mediaType="image"
                alt={exercise.name || t('questDetails.exerciseImageAlt', 'Exercise image')}
                onExpand={onExpandImage}
                onReport={() => onReportImage({ scope: 'exercise', exerciseIndex: index, mediaType: 'image', mediaUrl: exercise.imageUrl, imageUrl: exercise.imageUrl })}
                isReporting={isImageReporting({ scope: 'exercise', exerciseIndex: index, mediaType: 'image', imageUrl: exercise.imageUrl })}
                isReported={isImageReported({ scope: 'exercise', exerciseIndex: index, mediaType: 'image', imageUrl: exercise.imageUrl })}
                t={t}
              >
                <Image
                  src={exercise.imageUrl}
                  alt={exercise.name || t('questDetails.exerciseImageAlt', 'Exercise image')}
                  transformation={[{ height: 192, width: 500, c: "at_least", dpr: 2, q: 80 }]}
                  className="w-full h-64 object-cover"
                />
              </QuestImageFrame>
            ) : (
              <MediaUnavailablePlaceholder />
            )}
          </div>
          <p className="font-semibold text-white capitalize">{exercise.name}</p>
          <div className="flex justify-around text-sm mt-2 text-gray-300">
            <span>{t('questDetails.sets', 'Sets')}: <span className="font-bold text-cyan-400">{exercise.sets}</span></span>
            <span>{t('questDetails.reps', 'Reps')}: <span className="font-bold text-cyan-400">{exercise.reps}</span></span>
            <span>{t('questDetails.rest', 'Rest')}: <span className="font-bold text-cyan-400">{Number.isFinite(exercise.rest) ? t('questDetails.seconds', '{count} seconds', { count: exercise.rest }) : exercise.rest || t('questDetails.na', 'N/A')}</span></span>
          </div>
          <div className="text-center text-sm mt-2 text-gray-300">
            <span>{t('questDetails.suggestedWeight', 'Suggested Weight')}: <span className="font-bold text-cyan-400">{exercise.suggestedWeight || t('questDetails.na', 'N/A')}</span></span>
          </div>
          {exercise.instructions && (
            <div className="mt-3 border-t border-white/10 pt-3">
              {(() => {
                const raw = String(exercise.instructions).trim();
                if (!raw) return null;

                const splitByNewline = raw
                  .split('\n')
                  .map(line => line.trim())
                  .filter(Boolean);

                let steps = splitByNewline;
                if (steps.length <= 1) {
                  const sanitized = raw.replace(/^\s*-\s*/, '');
                  steps = sanitized
                    .split(/\s+-\s+/)
                    .map(line => line.trim())
                    .filter(Boolean);
                }

                if (!steps.length) return null;

                return (
                  <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                    {steps.map((line, idx) => {
                      const withoutBullet = line.replace(/^-+\s*/, '').trim();
                      return <li key={idx}>{withoutBullet}</li>;
                    })}
                  </ul>
                );
              })()}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// Helper component to render Meal details
const MealDetails = ({ details, isLoading, onExpandImage, onReportImage, isImageReporting, isImageReported, t }) => (
  <div className="space-y-4">
    {/* NEW: Image rendering logic */}
    <div className="mb-3">
      {isLoading ? (
        <ImagePlaceholder />
      ) : details.imageUrl ? (
        <QuestImageFrame
          mediaUrl={details.imageUrl}
          mediaType="image"
          alt={details.name || t('questDetails.mealImageAlt', 'Meal image')}
          onExpand={onExpandImage}
          onReport={() => onReportImage({ scope: 'meal', mediaType: 'image', mediaUrl: details.imageUrl, imageUrl: details.imageUrl })}
          isReporting={isImageReporting({ scope: 'meal', mediaType: 'image', imageUrl: details.imageUrl })}
          isReported={isImageReported({ scope: 'meal', mediaType: 'image', imageUrl: details.imageUrl })}
          t={t}
        >
          <Image
            src={details.imageUrl} // Correct prop is src
            alt={details.name || t('questDetails.mealImageAlt', 'Meal image')}
            transformation={[{ height: 192, width: 500, c: "at_least", dpr: 2, q: 80 }]}
            className="w-full h-64 object-cover"
          />
        </QuestImageFrame>
      ) : (
        <MediaUnavailablePlaceholder />
      )}
    </div>
    <div className="bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl text-center">
      <p className="text-sm text-gray-400">{t('questDetails.estimatedCalories', 'Estimated Calories')}</p>
      <p className="font-bold text-white text-xl">{details.totalCalories ? `${details.totalCalories} kcal` : t('questDetails.na', 'N/A')}</p>
      {details.macronutrients && (
        <div className="flex justify-around text-xs mt-3 text-gray-300 bg-slate-900/40 p-2 rounded-lg border border-white/5">
          <span>{t('questDetails.protein', 'Protein')}: <span className="font-bold text-cyan-400">{details.macronutrients.protein}</span></span>
          <span>{t('questDetails.carbs', 'Carbs')}: <span className="font-bold text-cyan-400">{details.macronutrients.carbs}</span></span>
          <span>{t('questDetails.fat', 'Fat')}: <span className="font-bold text-cyan-400">{details.macronutrients.fat}</span></span>
        </div>
      )}
    </div>
    <div>
      <h5 className="font-semibold text-cyan-400 mb-2">{t('questDetails.ingredients', 'Ingredients')}</h5>
      <ul className="list-disc list-inside space-y-1 text-gray-300 bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl">
        {details.ingredients?.map((item, index) => <li key={index}>{item}</li>)}
      </ul>
    </div>
    <div>
      <h5 className="font-semibold text-cyan-400 mb-2">{t('questDetails.instructions', 'Instructions')}</h5>
      <div className="text-gray-300 bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl">
        <ol className="list-decimal list-inside space-y-2">
          {details.instructions && details.instructions.split('\n').filter(line => line.trim() !== '').map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ol>
      </div>
    </div>
  </div>
);

const MindDetails = ({ details, t }) => {
  const rounds = Array.isArray(details?.session?.rounds) ? details.session.rounds : [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-center">
        <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
          <p className="text-sm text-gray-400">{t('questDetails.time', 'Time')}</p>
          <p className="font-bold text-white">{details.estimatedTime || t('questDetails.na', 'N/A')}</p>
        </div>
        <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
          <p className="text-sm text-gray-400">{t('questDetails.roundsLabel', 'Rounds')}</p>
          <p className="font-bold text-white">{details?.session?.roundsCount || rounds.length || 0}</p>
        </div>
      </div>

      <div className="bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl">
        {details.trackLabel && (
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200/70">{details.trackLabel}</p>
        )}
        <p className="text-sm text-cyan-300 font-semibold">{details.archetypeLabel || t('questDetails.mindArchetype', 'Logic ordering')}</p>
        <p className="mt-2 text-sm text-gray-300">{details.summary || t('questDetails.mindSummary', 'Complete four short ordering rounds to lock the quest for today.')}</p>
        {details.repeatDistanceDays ? (
          <p className="mt-2 text-xs text-gray-400">
            {t('questDetails.mindRepeatDistance', 'Exact scenarios stay out of rotation for {days} days.', { days: details.repeatDistanceDays })}
          </p>
        ) : null}
        {Array.isArray(details.howToPlay) && details.howToPlay.length > 0 && (
          <ul className="mt-3 list-disc list-inside space-y-1 text-sm text-gray-300">
            {details.howToPlay.map((step, index) => <li key={`mind-step-${index}`}>{step}</li>)}
          </ul>
        )}
      </div>

      <div className="space-y-3">
        {rounds.map((round, index) => (
          <div key={round.id || index} className="bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200/70">
                  {t('questDetails.roundLabel', 'Round {number}', { number: index + 1 })}
                </p>
                <p className="mt-1 font-semibold text-white">{round.title}</p>
              </div>
              <span className="rounded-full border border-cyan-200/20 bg-cyan-300/10 px-3 py-1 text-[11px] font-semibold text-cyan-100">
                {round.difficultyLabel || ''}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-300">{round.subtitle || round.prompt}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const HealthKitQuestDetails = ({ quest, details, t }) => {
  const healthkit = details?.healthkit || {};
  const key = healthkit.key;
  const currentValue = Number.isFinite(healthkit.currentValue) ? healthkit.currentValue : 0;
  const targetValue = Number.isFinite(healthkit.targetValue) ? healthkit.targetValue : 0;
  const progressValue = key === 'sleep'
    ? `${formatSleepMinutes(currentValue)} / ${formatSleepMinutes(targetValue)}`
    : `${currentValue.toLocaleString()} / ${targetValue.toLocaleString()} steps`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-center">
        <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
          <p className="text-sm text-gray-400">{t('questDetails.current', 'Current')}</p>
          <p className="font-bold text-white">{progressValue}</p>
        </div>
        <div className="bg-slate-950/50 border border-white/5 shadow-inner p-3 rounded-xl">
          <p className="text-sm text-gray-400">{t('questDetails.verification', 'Verification')}</p>
          <p className="font-bold text-white">{quest?.isLockedTeaser ? t('questDetails.locked', 'Locked') : t('questDetails.automatic', 'Automatic')}</p>
        </div>
      </div>

      <div className="bg-slate-950/50 border border-white/5 shadow-inner p-4 rounded-xl text-sm text-gray-300 leading-6">
        {quest?.isLockedTeaser ? (
          <>
            {t('questDetails.healthKitLockedBody', 'Enable HealthKit on iPhone in the Forza app to unlock this quest. Forza can then use your Apple Health data to track progress and mark the quest complete when the goal is recorded.')}
          </>
        ) : (
          <>
            {t('questDetails.healthKitActiveBody', 'This quest uses Apple Health data for verification. Sync runs in the iPhone app on app open and when returning to the foreground.')}
          </>
        )}
      </div>
    </div>
  );
};

const QuestDetailsModal = ({ quest, isOpen, onClose }) => {
  const { addToast } = useToast() || {};
  const { t } = useLocale();

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const [questWithImages, setQuestWithImages] = useState(quest);

  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const [expandedImage, setExpandedImage] = useState(null);

  const [reportingImageKeys, setReportingImageKeys] = useState(() => new Set());

  const [reportedImageKeys, setReportedImageKeys] = useState(() => new Set());



  // Sync state with props when the quest itself changes

  useEffect(() => {

    setQuestWithImages(quest);

    setIsAccordionOpen(false); // Close accordion when quest changes

    setIsLoadingImages(false); // Reset loading state

    setExpandedImage(null);

    setReportingImageKeys(new Set());

    setReportedImageKeys(new Set());

  }, [quest]);

  const isImageReporting = (payload) => reportingImageKeys.has(buildQuestImageReportKey(payload));

  const isImageReported = (payload) => reportedImageKeys.has(buildQuestImageReportKey(payload));

  const handleExpandImage = (image) => {
    setExpandedImage(image);
  };

  const handleReportImage = async (payload) => {
    if (!quest?._id || !(payload?.mediaUrl || payload?.imageUrl)) return;

    const reportKey = buildQuestImageReportKey(payload);
    if (reportingImageKeys.has(reportKey) || reportedImageKeys.has(reportKey)) return;

    setReportingImageKeys((prev) => new Set(prev).add(reportKey));
    try {
      await reportQuestImage(quest._id, payload);
      setReportedImageKeys((prev) => new Set(prev).add(reportKey));
      addToast?.(t('questDetails.imageReportedForReview', 'Image reported for review.'), 'success');
    } catch (error) {
      const message = error.response?.data?.message || t('questDetails.reportImageFailed', 'Failed to report image.');
      addToast?.(message, 'error');
    } finally {
      setReportingImageKeys((prev) => {
        const next = new Set(prev);
        next.delete(reportKey);
        return next;
      });
    }
  };



  const toggleAccordion = () => {

    const needsMediaFetch = (() => {
      if (!questWithImages?.detailedDescription) return false;
      if (questWithImages.type === 'HealthKit') {
        return false;
      }
      if (questWithImages.type === 'Meal') {
        return !questWithImages.detailedDescription.imageUrl;
      }
      const exercises = questWithImages.detailedDescription.exercises || [];
      if (!exercises.length) return false;
      return exercises.some(exercise => !exercise.imageUrl || !exercise.videoUrl);
    })();

    if (!isAccordionOpen && needsMediaFetch && !isLoadingImages) {
      setIsLoadingImages(true);
      getImagesForQuest(quest._id)
        .then(response => {
          if (response.data._id === quest._id) {
            setQuestWithImages(response.data);
          }
        })
        .catch(error => {
          console.error("Failed to fetch quest media:", error);
        })
        .finally(() => {
          setIsLoadingImages(false);
        });
    }

    setIsAccordionOpen(!isAccordionOpen);

  };



  if (!quest) return null;



  const questIcons = {

    'Workout': <BicepIcon className="w-5 h-5" />,

    'Mind': <Brain className="w-5 h-5" />,

    'Mindfulness': <MeditateIcon className="w-5 h-5" />,

    'Meal': <CutleryIcon className="w-5 h-5" />,

    'HealthKit': quest?.detailedDescription?.healthkit?.key === 'sleep'
      ? <MoonIcon className="w-5 h-5" />
      : <FootstepsIcon className="w-5 h-5" />,

  };



  return (

    <>

      <Modal isOpen={isOpen} onClose={onClose} title={quest.name} size="md">

        <div className="space-y-4">

          <div>

            <div className="flex items-center space-x-2 text-cyan-400 mb-2">

              {questIcons[quest.type]}

              <h4 className="text-lg font-semibold">{t('questDetails.detailsTitle', '{type} Details', { type: quest.type })}</h4>

            </div>

            <p className="text-gray-300 whitespace-pre-line">{quest.description}</p>

          </div>



          {quest.detailedDescription && (

            <div className="border-t border-white/10 pt-4 ">

              <button

                onClick={toggleAccordion}

                className={`w-full flex justify-between items-center p-3 rounded-xl font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${isAccordionOpen

                  ? 'bg-cyan-500/80 text-white border border-cyan-400/50 shadow-inner' // Open state

                  : 'bg-slate-950/50 border border-white/5 text-white hover:bg-slate-800/80 hover:border-white/10' // Closed state with correct hover

                  }`}>

                <span className="font-semibold text-white">

                  {quest.type === 'Meal'
                    ? t('questDetails.viewRecipe', 'View Recipe & Nutrition')
                    : quest.type === 'Mind'
                      ? t('questDetails.viewSession', 'View Mind Session')
                    : quest.type === 'HealthKit'
                      ? t('questDetails.viewHealth', 'View Health Verification')
                      : t('questDetails.viewRoutine', 'View Full Routine')}

                </span>

                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${isAccordionOpen ? 'rotate-180' : ''}`} />

              </button>



              <div

                className={

                  isAccordionOpen

                    ? "transition-[max-height] duration-300 ease-out max-h-[60vh] overflow-y-auto overscroll-contain"

                    : "transition-[max-height] duration-300 ease-out max-h-0 overflow-hidden"

                }

              >

                <div className="p-4">

                  <ImageKitProvider urlEndpoint={config.imagekit.urlEndpoint}>

                    {quest.type === 'Mind'
                      ? <MindDetails details={questWithImages.detailedDescription} t={t} />
                      : quest.type === 'Workout' || quest.type === 'Mindfulness'
                      ? (
                        <WorkoutDetails
                          details={questWithImages.detailedDescription}
                          isLoading={isLoadingImages}
                          onExpandImage={handleExpandImage}
                          onReportImage={handleReportImage}
                          isImageReporting={isImageReporting}
                          isImageReported={isImageReported}
                          t={t}
                        />
                      )
                      : quest.type === 'HealthKit'
                        ? <HealthKitQuestDetails quest={questWithImages} details={questWithImages.detailedDescription} t={t} />
                        : (
                          <MealDetails
                            details={questWithImages.detailedDescription}
                            isLoading={isLoadingImages}
                            onExpandImage={handleExpandImage}
                            onReportImage={handleReportImage}
                            isImageReporting={isImageReporting}
                            isImageReported={isImageReported}
                            t={t}
                          />
                        )}

                  </ImageKitProvider>

                </div>

              </div>

            </div>

          )}

        </div>

      </Modal>

      {expandedImage ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={expandedImage.alt || t('questDetails.expandedMedia', 'Expanded quest media')}
        >
          <button
            type="button"
            onClick={() => setExpandedImage(null)}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-slate-950/85 text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            aria-label={t('questDetails.closeExpandedImage', 'Close expanded image')}
          >
            <X className="h-5 w-5" />
          </button>
          {expandedImage.mediaType === 'video' ? (
            <video
              src={getMediaUrl(expandedImage.mediaUrl, config.media.workerBase)}
              controls
              playsInline
              autoPlay
              className="max-h-[88vh] max-w-[92vw] object-contain"
            />
          ) : (
            <img
              src={resolveImageKitUrl(expandedImage.mediaUrl, config.imagekit.urlEndpoint)}
              alt={expandedImage.alt || t('questDetails.expandedMedia', 'Expanded quest media')}
              className="max-h-[88vh] max-w-[92vw] object-contain"
            />
          )}
        </div>
      ) : null}

    </>

  );

};

export default QuestDetailsModal;
